/**
 * 解决 Element UI 等组件库中 non-passive mousewheel event listener 警告的工具
 * 该警告通常发生在浏览器控制台，提示：
 * "[Violation] Added non-passive event listener to a scroll-blocking 'mousewheel' event."
 */

// 重写 addEventListener 方法以自动添加 passive 选项
const passiveSupport = (function() {
  let supportsPassive = false;
  try {
    const opts = Object.defineProperty({}, 'passive', {
      get: function() {
        supportsPassive = true;
        return undefined; // 添加返回值以满足ESLint getter-return规则
      }
    });
    window.addEventListener('testPassive', null, opts);
    window.removeEventListener('testPassive', null, opts);
  } catch (e) {
    // 静默失败，不输出控制台信息
  }
  return supportsPassive;
})();

// 主要滚动事件类型
const scrollEvents = ['wheel', 'mousewheel', 'touchstart', 'touchmove'];

// 保存原始方法
const originalAddEventListener = EventTarget.prototype.addEventListener;
const originalRemoveEventListener = EventTarget.prototype.removeEventListener;

// 存储需要添加 passive 选项的事件监听器
const passiveListenersMap = new WeakMap();

/**
 * 重写 addEventListener 方法
 * 自动为滚动相关事件添加 passive: true 选项
 */
EventTarget.prototype.addEventListener = function(type, listener, options) {
  // 对于滚动相关的事件，如果浏览器支持 passive 且没有明确指定 passive 选项，自动添加 passive: true
  if (
    passiveSupport &&
    scrollEvents.includes(type) &&
    (typeof options === 'undefined' || typeof options === 'boolean' || !options.passive)
  ) {
    // 存储原始监听器，以便 removeEventListener 时能够正确移除
    if (!passiveListenersMap.has(this)) {
      passiveListenersMap.set(this, new Map());
    }
    
    const targetMap = passiveListenersMap.get(this);
    if (!targetMap.has(listener)) {
      targetMap.set(listener, new Map());
    }
    
    const listenerMap = targetMap.get(listener);
    listenerMap.set(type, options);
    
    // 使用 passive: true 选项
    const passiveOptions = typeof options === 'object' ? { ...options, passive: true } : { passive: true };
    originalAddEventListener.call(this, type, listener, passiveOptions);
  } else {
    // 其他事件保持原样
    originalAddEventListener.call(this, type, listener, options);
  }
};

/**
 * 重写 removeEventListener 方法
 * 确保能够正确移除通过重写的 addEventListener 添加的监听器
 */
EventTarget.prototype.removeEventListener = function(type, listener, options) {
  // 尝试获取原始选项
  let originalOptions = options;
  if (
    passiveSupport &&
    scrollEvents.includes(type) &&
    passiveListenersMap.has(this) &&
    passiveListenersMap.get(this).has(listener) &&
    passiveListenersMap.get(this).get(listener).has(type)
  ) {
    originalOptions = passiveListenersMap.get(this).get(listener).get(type);
    
    // 清理映射
    passiveListenersMap.get(this).get(listener).delete(type);
    if (passiveListenersMap.get(this).get(listener).size === 0) {
      passiveListenersMap.get(this).delete(listener);
    }
    if (passiveListenersMap.get(this).size === 0) {
      passiveListenersMap.delete(this);
    }
  }
  
  // 移除监听器
  originalRemoveEventListener.call(this, type, listener, originalOptions);
};

export default {
  passiveSupport,
  scrollEvents
};